import { useBookingFlowData } from './useBookingFlowData';
import useBookAppointmentMutation from './useBookAppointmentMutation';
import useRescheduleAppointmentMutation from './useRescheduleAppointmentMutation';
import useCreateCaseMutation from './useCreateCaseMutation';
import useCaseEligibilityPolicyMutation from './useCaseEligibilityPolicyMutation';
import { useRuntimeConfig } from './Runtime';
import useSystemType from 'vl-core/hooks/useSystemType';
import { sendToParticipants } from '../lib/sendToParticipants';

type ConfirmStrategy = () => Promise<void>;

const STRATEGY_KEYS = {
  NEW_CASE_APPOINTMENT: 'NEW_CASE_APPOINTMENT',
  BOOK: 'BOOK',
  RESCHEDULE: 'RESCHEDULE'
} as const;

type StrategyKey = (typeof STRATEGY_KEYS)[keyof typeof STRATEGY_KEYS];

const useConfirmAppointmentStrategy = () => {
  const { TWILIO_BACKEND_URL } = useRuntimeConfig();
  const isAdmin = useSystemType() === 'vl-admin';
  const { bookingData, caseData, patientData, previousAppointmentData } = useBookingFlowData();

  const {
    bookAppointment,
    isPending: isBookingAppointment,
    isError: isBookingError,
    error: bookingError,
    reset: resetBooking
  } = useBookAppointmentMutation();

  const {
    rescheduleAppointment,
    isPending: isReschedulingAppointment,
    isError: isReschedulingError,
    error: reschedulingError,
    reset: resetRescheduling
  } = useRescheduleAppointmentMutation();

  const {
    mutateAsync: createNewCase,
    isPending: isCreatingCase,
    isError: isCreateCaseError,
    error: createCaseError,
    reset: resetCreateCase
  } = useCreateCaseMutation();

  const { mutateAsync: updateCaseEligibilityPolicies, isPending: isUpdatingEligibilityPolicies } =
    useCaseEligibilityPolicyMutation();

  const newCaseStrategy: ConfirmStrategy = async () => {
    if (!caseData) throw new Error('Missing required new case details');
    if (!bookingData?.apptDate) throw new Error('Missing appointment date');
    if (!patientData?.user_guid) throw new Error('Missing patient data');
    if (!bookingData.clinician?.id) throw new Error('Missing clinician data');
    if (!bookingData.callType) throw new Error('Missing call type');

    const caseId = await createNewCase({
      pat_guid: caseData.patient_user_guid,
      speccat: caseData.spec_cat_code,
      specarea: caseData.case_spec_area_code || '',
      caseUrgency: caseData.urg_code,
      client_case_id: caseData.client_case_id,
      first_appt: true,
      ...('cohort_number' in caseData && { cohort_number: caseData.cohort_number })
    });

    await bookAppointment({
      user_guid: patientData.user_guid,
      clin_guid: bookingData.clinician.id,
      case_id: caseId,
      date: new Date(bookingData.apptDate),
      type: bookingData.apptType,
      call_type: bookingData.callType
    });

    if ('appliedEligibilityChecks' in caseData && caseData.appliedEligibilityChecks?.length) {
      const acceptedPolicies = caseData.appliedEligibilityChecks.map((policyCode) => ({
        policyCode,
        applied: true
      }));

      try {
        await updateCaseEligibilityPolicies({ caseId, policies: acceptedPolicies });
      } catch (error) {
        console.log('Error updating case eligibility policies: ', error);
      }
    }
  };

  const bookStrategy: ConfirmStrategy = async () => {
    if (!bookingData?.apptDate) throw new Error('Missing appointment date');
    if (!bookingData.caseId) throw new Error('Missing case ID');
    if (!patientData?.user_guid) throw new Error('Missing patient data');
    if (!bookingData.clinician?.id) throw new Error('Missing clinician data');
    if (!bookingData.callType) throw new Error('Missing call type');

    const patientApptTypeCode =
      caseData && 'patient_appt_type_code' in caseData ? caseData.patient_appt_type_code : undefined;

    await bookAppointment({
      user_guid: patientData.user_guid,
      clin_guid: bookingData.clinician.id,
      case_id: bookingData.caseId,
      date: new Date(bookingData.apptDate),
      type: patientApptTypeCode || bookingData.apptType,
      call_type: bookingData.callType
    });
  };

  const rescheduleStrategy: ConfirmStrategy = async () => {
    if (!bookingData?.apptDate) throw new Error('Missing appointment date');
    if (!bookingData.previousApptId) throw new Error('Missing previous appointment ID');
    if (!patientData?.user_guid) throw new Error('Missing patient data');
    if (!bookingData.clinician?.id) throw new Error('Missing clinician data');
    if (!bookingData.callType) throw new Error('Missing call type');

    await rescheduleAppointment({
      user_guid: patientData.user_guid,
      appt_id: bookingData.previousApptId,
      date: new Date(bookingData.apptDate),
      clin_user_guid: bookingData.clinician.id,
      call_type_code: bookingData.callType,
      reason: bookingData.rescheduleReason
    });

    const isRescheduledForSameClinician = bookingData.clinician.id === previousAppointmentData?.clinician_user_guid;

    if (previousAppointmentData && isAdmin && !isRescheduledForSameClinician) {
      const message = {
        clinicianChangedFrom: previousAppointmentData?.clinician_user_guid,
        clinicianChangedTo: bookingData.clinician.id,
        newClinicianName: bookingData.clinician.name
      };

      try {
        await sendToParticipants(
          String(bookingData.previousApptId),
          message,
          TWILIO_BACKEND_URL,
          bookingData.clinician.id,
          bookingData.patientId
        );
      } catch (error) {
        console.log('Error sending Twillio event for rescheduled appointment: ', error);
      }
    }
  };

  const strategyMap: Record<StrategyKey, ConfirmStrategy> = {
    [STRATEGY_KEYS.NEW_CASE_APPOINTMENT]: newCaseStrategy,
    [STRATEGY_KEYS.RESCHEDULE]: rescheduleStrategy,
    [STRATEGY_KEYS.BOOK]: bookStrategy
  };

  const getStrategyKey = (): StrategyKey => {
    if (bookingData?.isNewCase) return STRATEGY_KEYS.NEW_CASE_APPOINTMENT;
    if (bookingData?.bookingType === 'RESCHEDULE') return STRATEGY_KEYS.RESCHEDULE;
    return STRATEGY_KEYS.BOOK;
  };

  const bookingStrategy = getStrategyKey();

  const confirm = () => strategyMap[bookingStrategy]();

  const reset = () => {
    resetBooking();
    resetRescheduling();
    resetCreateCase();
  };

  return {
    confirm,
    reset,
    isPending: isBookingAppointment || isReschedulingAppointment || isCreatingCase || isUpdatingEligibilityPolicies,
    isError: isBookingError || isReschedulingError || isCreateCaseError,
    error: bookingError ?? reschedulingError ?? createCaseError
  };
};

export default useConfirmAppointmentStrategy;
